/* Shutter Bombs — custom motion system */

(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // Treat as touch if no fine pointer AND no hover — use both checks for reliability
  const noFinePointer = window.matchMedia('(pointer: coarse)').matches;
  const noHover = window.matchMedia('(hover: none)').matches;
  const isTouch = noFinePointer || noHover;

  // -------- Scroll accent color cycling --------
  const ACCENTS = [
    { hex: '#e5342f', rgb: '229, 52, 47' },
    { hex: '#f26a21', rgb: '242, 106, 33' },
    { hex: '#f2c94c', rgb: '242, 201, 76' },
    { hex: '#2f9e44', rgb: '47, 158, 68' },
    { hex: '#1e72e8', rgb: '30, 114, 232' },
    { hex: '#7a42d6', rgb: '122, 66, 214' },
    { hex: '#ff5da0', rgb: '255, 93, 160' },
  ];

  let currentAccent = 0;
  let rafScheduled = false;

  // Throttle accent cycling — was rAF on every scroll event (60+ recalcs/s).
  // 100ms cap is plenty for color cycling and dramatically cuts main-thread time.
  let lastAccentRun = 0;
  function updateAccent() {
    const now = performance.now();
    if (now - lastAccentRun < 100) return;
    lastAccentRun = now;
    if (rafScheduled) return;
    rafScheduled = true;
    requestAnimationFrame(() => {
      const scrollPct = window.scrollY / Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const idx = Math.min(ACCENTS.length - 1, Math.floor(scrollPct * ACCENTS.length));
      if (idx !== currentAccent) {
        currentAccent = idx;
        document.documentElement.style.setProperty('--sb-accent', ACCENTS[idx].hex);
        document.documentElement.style.setProperty('--sb-accent-rgb', ACCENTS[idx].rgb);
      }
      rafScheduled = false;
    });
  }

  // Skip accent cycling on reduced-motion AND on touch (perf cost not worth it on phones).
  if (!prefersReducedMotion && !isTouch) {
    window.addEventListener('scroll', updateAccent, { passive: true });
  }

  // Custom cursor disabled — using browser default

  // -------- Text scramble on hover --------
  const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#@!';

  function scrambleText(el, original) {
    let frame = 0;
    const totalFrames = 14;
    let raf;

    function tick() {
      frame++;
      const progress = frame / totalFrames;
      let out = '';
      for (let i = 0; i < original.length; i++) {
        if (original[i] === ' ') { out += ' '; continue; }
        if (i / original.length < progress) {
          out += original[i];
        } else {
          out += CHARS[Math.floor(Math.random() * CHARS.length)];
        }
      }
      el.textContent = out;
      if (frame < totalFrames) {
        raf = requestAnimationFrame(tick);
      } else {
        el.textContent = original;
      }
    }
    cancelAnimationFrame(raf);
    requestAnimationFrame(tick);
  }

  function setupScramble() {
    document.querySelectorAll('[data-sb-scramble]').forEach((el) => {
      const original = el.textContent.trim();
      el.addEventListener('mouseenter', () => scrambleText(el, original));
    });
  }

  // -------- Parallax on scroll (desktop only — too costly on touch devices) --------
  function setupParallax() {
    const parallaxEls = document.querySelectorAll('[data-sb-parallax]');
    if (!parallaxEls.length || prefersReducedMotion || isTouch) return;

    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        parallaxEls.forEach((el) => {
          const speed = parseFloat(el.dataset.sbParallax) || 0.15;
          const rect = el.getBoundingClientRect();
          const viewCenter = window.innerHeight / 2;
          const elCenter = rect.top + rect.height / 2;
          const offset = (viewCenter - elCenter) * speed;
          el.style.transform = `translateY(${offset}px)`;
        });
        ticking = false;
      });
    }, { passive: true });
  }

  // -------- Persona tile: tilt on mousemove (desktop hover-capable only) --------
  function setupTilt() {
    if (isTouch || prefersReducedMotion) return;
    document.querySelectorAll('.sb-persona__tile').forEach((tile) => {
      let tiltRaf = 0;
      tile.addEventListener('mousemove', (e) => {
        if (tiltRaf) return;
        tiltRaf = requestAnimationFrame(() => {
          const r = tile.getBoundingClientRect();
          const x = (e.clientX - r.left) / r.width - 0.5;
          const y = (e.clientY - r.top) / r.height - 0.5;
          tile.style.transform = `perspective(800px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) scale(1.02)`;
          tiltRaf = 0;
        });
      });
      tile.addEventListener('mouseleave', () => {
        tile.style.transform = '';
      });
    });
  }

  // -------- Reveal on scroll --------
  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add('sb-in');
        revealObserver.unobserve(e.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

  function setupReveal() {
    document.querySelectorAll('.sb-reveal:not(.sb-in)').forEach((el) => revealObserver.observe(el));
  }

  // -------- Count-up stats --------
  function setFinal(el) {
    const target = parseFloat(el.dataset.sbCount);
    const suffix = el.dataset.sbSuffix || '';
    const prefix = el.dataset.sbPrefix || '';
    const raw = el.dataset.sbRaw === '1';
    el.textContent = prefix + (raw ? String(target) : target.toLocaleString('en-US')) + suffix;
  }

  const countObserver = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (!e.isIntersecting) return;
      const el = e.target;
      if (prefersReducedMotion) {
        setFinal(el);
        countObserver.unobserve(el);
        return;
      }
      const target = parseFloat(el.dataset.sbCount);
      const suffix = el.dataset.sbSuffix || '';
      const prefix = el.dataset.sbPrefix || '';
      const raw = el.dataset.sbRaw === '1';
      const dur = parseInt(el.dataset.sbDur || 1600, 10);
      const start = performance.now();

      function tick(now) {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        const value = Math.round(target * eased);
        el.textContent = prefix + (raw ? String(value) : value.toLocaleString('en-US')) + suffix;
        if (t < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
      countObserver.unobserve(el);
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });

  function setupCount() {
    document.querySelectorAll('[data-sb-count]').forEach((el) => countObserver.observe(el));
  }

  // -------- Boot --------
  function boot() {
    document.body.classList.add('sb-loaded');
    setupReveal();
    setupCount();
    setupScramble();
    setupParallax();
    setupTilt();
    updateAccent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', () => {
    setupReveal();
    setupCount();
    setupScramble();
    setupTilt();
  });
})();
